import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Button,
  Table,
  Space,
  DatePicker,
  Typography,
  Spin,
  Alert,
  Select,
  Collapse,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useCurrency } from '@/contexts/CurrencyContext';
import { getSettlementRegister, getOpenSources, type SettlementRegisterRow, type SettlementRegisterFilters, type SettlementOpenSource } from '@/services/settlement.service';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';
import type { ReportParams } from '@/services/reportDocuments.service';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

export const SettlementRegisterPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const { formatCurrency } = useCurrency();
  const [data, setData] = useState<SettlementRegisterRow[]>([]);
  const [openSources, setOpenSources] = useState<SettlementOpenSource[]>([]);
  const [loading, setLoading] = useState(false);
  const [openSourcesLoading, setOpenSourcesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [partySide, setPartySide] = useState<string | undefined>(undefined);
  const [partyId, setPartyId] = useState<string | undefined>(undefined);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [settlementType, setSettlementType] = useState<string | undefined>(undefined);

  const partySideOptions = useMemo(
    () => [
      { value: 'AR', label: t('partySideAr') },
      { value: 'AP', label: t('partySideAp') },
    ],
    [t]
  );

  const settlementTypeOptions = useMemo(
    () => [
      { value: 'CustomerAdvance', label: t('settlementTypeCustomerAdvance') },
      { value: 'CustomerCreditNote', label: t('settlementTypeCustomerCreditNote') },
      { value: 'SupplierAdvance', label: t('settlementTypeSupplierAdvance') },
      { value: 'SupplierDebitNote', label: t('settlementTypeSupplierDebitNote') },
    ],
    [t]
  );

  const settlementTypeLabel = (value: string) => {
    const map: Record<string, string> = {
      CustomerAdvance: t('settlementTypeCustomerAdvance'),
      CustomerCreditNote: t('settlementTypeCustomerCreditNote'),
      SupplierAdvance: t('settlementTypeSupplierAdvance'),
      SupplierDebitNote: t('settlementTypeSupplierDebitNote'),
    };
    return map[value] ?? value;
  };

  const load = () => {
    setLoading(true);
    setError(null);
    const filters: SettlementRegisterFilters = {};
    if (partySide) filters.party_side = partySide;
    if (partyId) filters.party_id = partyId;
    if (dateRange?.[0]) filters.from_date = dateRange[0].format('YYYY-MM-DD');
    if (dateRange?.[1]) filters.to_date = dateRange[1].format('YYYY-MM-DD');
    if (settlementType) filters.settlement_type = settlementType;
    getSettlementRegister(filters)
      .then(setData)
      .catch((err: any) => setError(err?.message ?? t('errLoadSettlementRegister')))
      .finally(() => setLoading(false));
  };

  const loadOpenSources = () => {
    const side = partySide || 'AR';
    setOpenSourcesLoading(true);
    getOpenSources(side, partyId, ['CustomerCreditNote', 'CustomerAdvance'])
      .then(setOpenSources)
      .catch(() => setOpenSources([]))
      .finally(() => setOpenSourcesLoading(false));
  };

  const handleApply = () => {
    load();
    if (partySide === 'AR' || !partySide) loadOpenSources();
  };

  const docParams: ReportParams = {
    ...(partySide ? { party_side: partySide } : {}),
    ...(partyId ? { party_id: partyId } : {}),
    ...(settlementType ? { settlement_type: settlementType } : {}),
    ...(dateRange?.[0] ? { from: dateRange[0].format('YYYY-MM-DD') } : {}),
    ...(dateRange?.[1] ? { to: dateRange[1].format('YYYY-MM-DD') } : {}),
  };

  useEffect(() => {
    load();
    loadOpenSources();
  }, []);

  useEffect(() => {
    if (partySide === 'AR' || !partySide) {
      loadOpenSources();
    } else {
      setOpenSources([]);
    }
  }, [partySide, partyId]);

  const columns: ColumnsType<SettlementRegisterRow> = [
    { title: t('adjustmentDate'), dataIndex: 'adjustment_date', key: 'adjustment_date', width: 120 },
    {
      title: t('settlementType'),
      dataIndex: 'settlement_type',
      key: 'settlement_type',
      width: 160,
      render: (v: string) => settlementTypeLabel(v),
    },
    { title: t('partySide'), dataIndex: 'party_side', key: 'party_side', width: 80 },
    { title: t('sourceRef'), dataIndex: 'source_reference_number', key: 'source_reference_number', width: 140, ellipsis: true },
    { title: t('targetRef'), dataIndex: 'target_reference_number', key: 'target_reference_number', width: 140, ellipsis: true },
    {
      title: t('amount'),
      dataIndex: 'adjusted_amount',
      key: 'adjusted_amount',
      align: 'right',
      width: 120,
      render: (v: number) => formatCurrency(v ?? 0),
    },
    { title: t('party'), dataIndex: 'party_name', key: 'party_name', width: 220, ellipsis: true, render: (v: string | null, r) => v || r.party_id },
  ];

  return (
    <div className="settlement-register-page" style={{ padding: 24 }}>
      <div className="no-print">
        <Title level={4} style={{ marginBottom: 4 }}>{t('settlementRegister')}</Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          {t('settlementRegisterHint')}
        </Text>
      </div>
      <Card>
        <Space wrap className="no-print" style={{ marginBottom: 16 }}>
          <Select
            placeholder={t('partySideArAp')}
            allowClear
            style={{ width: 180 }}
            value={partySide}
            onChange={setPartySide}
            options={partySideOptions}
          />
          <Select
            placeholder={t('settlementType')}
            allowClear
            style={{ width: 200 }}
            value={settlementType}
            onChange={setSettlementType}
            options={settlementTypeOptions}
          />
          <RangePicker
            value={dateRange}
            onChange={(val) => setDateRange(val as [dayjs.Dayjs, dayjs.Dayjs] | null)}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleApply}>
            {t('apply')}
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => { load(); loadOpenSources(); }}>
            {tc('refresh')}
          </Button>
          <ReportDocumentActions reportKey="settlement-register" params={docParams} />
        </Space>
        {error && <Alert className="no-print" type="error" message={error} style={{ marginBottom: 16 }} />}
        <Alert className="no-print" type="info" message={t('settlementAllocationsInfo')} style={{ marginBottom: 16 }} />
        {(partySide === 'AR' || !partySide) && (
          <Collapse className="no-print" style={{ marginBottom: 16 }}>
            <Collapse.Panel header={t('openArSourcesAvailable', { count: openSources.length })} key="open">
              <Spin spinning={openSourcesLoading}>
                <Table
                  rowKey="id"
                  dataSource={openSources}
                  pagination={false}
                  size="small"
                  columns={[
                    { title: t('ref'), dataIndex: 'reference_number', key: 'ref', width: 140 },
                    {
                      title: t('type'),
                      dataIndex: 'source_type',
                      key: 'type',
                      width: 160,
                      render: (v: string) => settlementTypeLabel(v),
                    },
                    { title: t('date'), dataIndex: 'source_date', key: 'date', width: 110 },
                    { title: t('original'), dataIndex: 'original_amount', key: 'original', align: 'right' as const, width: 100, render: (v: number) => formatCurrency(v ?? 0) },
                    { title: t('settled'), dataIndex: 'settled_amount', key: 'settled', align: 'right' as const, width: 100, render: (v: number) => formatCurrency(v ?? 0) },
                    { title: t('openCol'), dataIndex: 'open_amount', key: 'open', align: 'right' as const, width: 100, render: (v: number) => formatCurrency(v ?? 0) },
                    { title: t('status'), dataIndex: 'status', key: 'status', width: 100 },
                  ]}
                />
              </Spin>
            </Collapse.Panel>
          </Collapse>
        )}
        <Spin spinning={loading}>
          <Table
            rowKey="id"
            columns={columns}
            dataSource={data}
            sticky={{ offsetHeader: 0 }}
            pagination={{
              defaultPageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => t('totalRows', { count: total }),
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            scroll={{ x: 1100, y: 480 }}
            size="small"
          />
        </Spin>
      </Card>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .settlement-register-page { padding: 0; }
        }
      `}</style>
    </div>
  );
};
